import AccountBalanceWalletIcon from '@material-design-icons/svg/filled/account_balance_wallet.svg?react';
import ContentCopyIcon from '@material-design-icons/svg/filled/content_copy.svg?react';
import LaunchIcon from '@material-design-icons/svg/filled/launch.svg?react';
import LogoutIcon from '@material-design-icons/svg/filled/logout.svg?react';
import MoreVertIcon from '@material-design-icons/svg/filled/more_vert.svg?react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { getSolanaExplorerUrl } from '../../utils/solanaUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const SidebarWalletConnector = () => {
  const { publicKey, disconnect, connected, wallet } = useWallet();
  const { setVisible } = useWalletModal();
  const previousConnected = useRef(connected);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip toast on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousConnected.current = connected;
      return;
    }

    if (connected && !previousConnected.current && wallet) {
      toast.success(`${wallet.adapter.name} wallet connected`);
    } else if (!connected && previousConnected.current) {
      toast.success('Wallet disconnected');
    }

    previousConnected.current = connected;
  }, [connected, wallet]);

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  const copyAddress = () => {
    if (publicKey) {
      navigator.clipboard.writeText(publicKey.toBase58());
      toast.success('Wallet address copied!');
    }
  };

  const openExplorer = () => {
    if (publicKey) {
      const explorerUrl = getSolanaExplorerUrl('address', publicKey.toBase58());
      window.open(explorerUrl, '_blank');
    }
  };

  const handleDisconnect = () => {
    disconnect();
    // Toast will be shown by the useEffect
  };

  if (!connected) {
    return (
      <div className="w-full px-2">
        <button
          type="button"
          onClick={() => setVisible(true)}
          className="w-60 h-9 px-4 py-5 bg-[#2a2a2a] rounded-xl inline-flex justify-center items-center gap-2 transition-all duration-300 ease-out hover:bg-[#3a3a3a] group relative"
        >
          <span className="text-white text-sm font-light leading-none">+</span>
          <span className="text-white text-sm font-medium font-['Inter'] leading-tight">
            Connect Wallet
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="w-full px-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="w-60 h-9 px-2 py-5 rounded-xl inline-flex justify-between items-center gap-2 transition-all duration-300 ease-out hover:bg-[#151717] hover:shadow-[0px_0.375px_0.375px_0px_inset_rgba(255,255,255,0.17)] group relative"
          >
            <div className="flex justify-start items-center gap-2.5">
              <div className="size-5 flex items-center justify-center">
                {wallet?.adapter.icon ? (
                  <img
                    src={wallet.adapter.icon}
                    alt={wallet.adapter.name}
                    className="size-5 object-contain"
                  />
                ) : (
                  <AccountBalanceWalletIcon className="size-5 fill-[#96A3A3] transition-all duration-300 ease-out group-hover:fill-white" />
                )}
              </div>
              <span className="text-sm font-medium font-['Inter'] leading-tight text-[#96A3A3] transition-all duration-300 ease-out group-hover:text-white">
                {publicKey
                  ? truncateAddress(publicKey.toBase58())
                  : 'Connected'}
              </span>
            </div>
            <MoreVertIcon className="size-4 fill-[#96A3A3] transition-all duration-300 ease-out group-hover:fill-white" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-48 bg-[#1a1a1a] border border-[#2a2a2a] text-white p-1 rounded-lg"
          align="end"
          sideOffset={4}
        >
          <DropdownMenuItem
            onClick={copyAddress}
            className="flex items-center gap-2.5 px-2 py-1.5 text-[#96A3A3] hover:text-white focus:bg-transparent focus:text-white cursor-pointer rounded transition-colors text-sm"
          >
            <ContentCopyIcon className="size-4 fill-current" />
            <span>Copy Address</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={openExplorer}
            className="flex items-center gap-2.5 px-2 py-1.5 text-[#96A3A3] hover:text-white focus:bg-transparent focus:text-white cursor-pointer rounded transition-colors text-sm"
          >
            <LaunchIcon className="size-4 fill-current" />
            <span>View in Explorer</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={handleDisconnect}
            className="flex items-center gap-2.5 px-2 py-1.5 text-[#ff4545] hover:text-[#ff6565] focus:bg-transparent focus:text-[#ff6565] cursor-pointer rounded transition-colors text-sm"
          >
            <LogoutIcon className="size-4 fill-current" />
            <span>Disconnect</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
